import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { nextHint, type Hint } from '../sudoku';
import { useGame } from '../game/useGame';
import type { GameState, InputConfig } from '../game/types';
import { useSettings } from '../settings/SettingsContext';
import { Board } from './Board';
import { GameControls } from './GameControls';
import { HintPanel } from './HintPanel';
import { NumberPad } from './NumberPad';
import { Timer } from './Timer';

interface PlayScreenProps {
  initial: GameState;
  title: ReactNode;
  onSolved?: (state: GameState) => void;
  solvedContent?: ReactNode;
  onNewGame?: () => void;
}

export function PlayScreen({ initial, title, onSolved, solvedContent, onNewGame }: PlayScreenProps) {
  const { settings } = useSettings();
  const getConfig = useCallback(
    (): InputConfig => ({
      autoRemoveNotes: settings.auto_remove_notes,
      mistakeMode: settings.mistake_mode,
    }),
    [settings.auto_remove_notes, settings.mistake_mode],
  );

  const { state, actions } = useGame(initial, { getConfig, onSolved });
  const [hint, setHint] = useState<Hint | null>(null);

  // A hint is stale once the board changes.
  useEffect(() => setHint(null), [state.cells]);

  const counts = useMemo(() => {
    const c: Record<number, number> = {};
    for (const v of state.cells) if (v) c[v] = (c[v] ?? 0) + 1;
    return c;
  }, [state.cells]);

  // --- Keyboard play (arrows + digits + erase + undo/redo). ---
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key >= '1' && e.key <= '9') actions.input(Number(e.key));
      else if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') actions.erase();
      else if (e.key === 'ArrowUp') actions.move('up');
      else if (e.key === 'ArrowDown') actions.move('down');
      else if (e.key === 'ArrowLeft') actions.move('left');
      else if (e.key === 'ArrowRight') actions.move('right');
      else if (e.key.toLowerCase() === 'n') actions.toggleNotesMode();
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) actions.redo();
        else actions.undo();
      } else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions]);

  const requestHint = () => {
    if (!settings.hints_enabled) return;
    const h = nextHint(state.givens, state.cells);
    if (h) {
      actions.useHint();
      setHint(h);
    }
  };

  const paused = !state.running && state.status === 'playing';

  return (
    <section className="play">
      <header className="play-head">
        <div className="play-title">{title}</div>
        <Timer
          elapsedMs={state.elapsedMs}
          running={state.running}
          onToggle={state.running ? actions.pause : actions.resume}
        />
      </header>

      {settings.mistake_mode === 'strike' && (
        <p className="strikes" aria-live="polite">
          Mistakes: {state.mistakes} / 3
        </p>
      )}

      <div className="play-body">
        <div className="board-col">
          <div className="board-wrap">
            <Board
              state={state}
              settings={settings}
              onSelect={actions.select}
              hintCells={hint?.cells ?? []}
            />
            {paused && (
              <button type="button" className="overlay pause-overlay" onClick={actions.resume}>
                Paused — tap to resume
              </button>
            )}
            {state.status === 'solved' && (
              <div className="overlay solved-overlay" role="status">
                <h2>Solved!</h2>
                {solvedContent}
                {onNewGame && (
                  <button type="button" onClick={onNewGame}>
                    New puzzle
                  </button>
                )}
              </div>
            )}
            {state.status === 'failed' && (
              <div className="overlay failed-overlay" role="status">
                <h2>Out of tries</h2>
                <p>Three mistakes reached.</p>
                {onNewGame && (
                  <button type="button" onClick={onNewGame}>
                    New puzzle
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="play-side">
          <NumberPad
            onNumber={actions.input}
            onErase={actions.erase}
            counts={counts}
            disabled={state.status !== 'playing'}
          />

          <GameControls
            notesMode={state.notesMode}
            canUndo={state.past.length > 0}
            canRedo={state.future.length > 0}
            hintsEnabled={settings.hints_enabled}
            onUndo={actions.undo}
            onRedo={actions.redo}
            onToggleNotes={actions.toggleNotesMode}
            onAutoNotes={actions.autoNotes}
            onHint={requestHint}
          />

          {hint && (
            <HintPanel hint={hint} onPlace={actions.placeHint} onClose={() => setHint(null)} />
          )}
        </div>
      </div>
    </section>
  );
}
